// api.js - Client API pour HoraPilot Frontend - VERSION MULTI-TENANT
// ============================================================================

import { API_URL } from '../config/apiBase';

// ============================================================================
// HELPERS
// ============================================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('superadmin_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};


// ✅ FIX — Verrou anti-boucle de redirection
let isRedirectingToLogin = false;

const redirectToLogin = (path = '/login') => {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  console.warn(`🔐 [API] Redirection vers ${path}`);

  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('company');
  localStorage.removeItem('superadmin_token');
  localStorage.removeItem('superadmin_user');
  localStorage.removeItem('superadmin_company');

  setTimeout(() => {
    window.location.href = path;
    setTimeout(() => { isRedirectingToLogin = false; }, 5000);
  }, 100);
};


// ============================================================================
// 🔥 NOUVEAU : Gestion erreurs multi-tenant
// ============================================================================
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur serveur' }));

    // ✅ FIX — 401 token expiré ou invalide (cas non géré avant → causait la boucle PWA)
    if (response.status === 401 && !error.shouldLogout && !error.code) {
      console.warn('🔐 [API] 401 reçu — token expiré ou invalide');
      redirectToLogin('/login');
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }

    // SESSION SUPPRIMÉE
    if (error.shouldLogout || error.code === 'SESSION_DELETED') {
      console.warn('⚠️ [API] Session supprimée par le serveur, déconnexion...');
      redirectToLogin('/login');
      throw new Error('Session expirée ou supprimée par une autre connexion');
    }

    // COMPANY BLOQUÉE
    if (error.code === 'COMPANY_BLOCKED') {
      console.error('🚫 [API] Entreprise bloquée');
      redirectToLogin('/login?error=company_blocked');
      throw new Error('Votre compte entreprise est bloqué. Merci de régulariser le mois impayé, puis de contacter le support afin d\'être débloqué.');
    }

    // ABONNEMENT SUSPENDU
    if (error.code === 'COMPANY_SUSPENDED') {
      console.error('⏸️ [API] Abonnement suspendu');
      redirectToLogin('/login?error=company_suspended');
      throw new Error('Votre abonnement est suspendu. Veuillez renouveler.');
    }

    // PÉRIODE D'ESSAI EXPIRÉE
    if (error.code === 'COMPANY_EXPIRED') {
      console.error('⏱️ [API] Période d\'essai expirée');
      redirectToLogin('/login?error=company_expired');
      throw new Error('Votre période d\'essai est expirée. Contactez le support.');
    }

    throw new Error(error.message || 'Une erreur est survenue');
  }
  return response.json();
};

// ============================================================================
// API OBJECT
// ============================================================================

const api = {

  // ==========================================================================
  // AUTHENTIFICATION - 🔥 MODIFIÉ (une seule version avec credentials)
  // ==========================================================================

  auth: {
    login: async (email, password) => {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      return handleResponse(response);
    },

    // ✅ NOUVEAU : Changement de mot de passe à la première connexion
    changePasswordFirstLogin: async (currentPassword, newPassword) => {
      console.log('🔐 [API] Changement mot de passe première connexion...');

      const response = await fetch(`${API_URL}/api/auth/change-password-first-login`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        // Propager le vrai message backend directement
        throw new Error(data.error || data.message || 'Erreur lors du changement du mot de passe');
      }

      console.log('✅ [API] Mot de passe changé avec succès');
      return data;
    },

    logout: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      window.location.href = '/login';
    }
  },

  // ==========================================================================
  // EMPLOYÉS
  // ==========================================================================

  employees: {

    // GET - Liste tous les employés
    getAll: async (params = {}) => {
      const { page = 1, limit = 10, search = '', department = '', status = '', contract_type = '', contract_expiry = '' } = params;
      const query = new URLSearchParams({ page, limit, search, department, status, contract_type, contract_expiry }).toString();
      const response = await fetch(`${API_URL}/api/employees?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    getDepartmentStats: async () => {
      const response = await fetch(`${API_URL}/api/employees/department-stats`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // GET - Détails d'un employé
    getOne: async (id) => {
      const response = await fetch(`${API_URL}/api/employees/${id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // POST - Créer un employé
    create: async (employeeData) => {
      try {
        console.log('📤 Création employé:', employeeData);

        const response = await fetch(`${API_URL}/api/employees`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(employeeData)
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur création employé:', error);
        throw error;
      }
    },

    // PUT - Mettre à jour un employé
    update: async (id, employeeData) => {
      try {
        console.log('📤 Mise à jour employé:', id);

        const response = await fetch(`${API_URL}/api/employees/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(employeeData)
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur update employé:', error);
        throw error;
      }
    },

    // DELETE - Supprimer un employé
    delete: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/employees/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur suppression employé:', error);
        throw error;
      }
    },

    // POST - Upload photo
    uploadPhoto: async (employeeId, formData) => {
      try {
        console.log('📸 Upload photo pour employé:', employeeId);

        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': token ? `Bearer ${token}` : ''
        };

        const response = await fetch(`${API_URL}/api/employees/${employeeId}/photo`, {
          method: 'POST',
          headers: headers,
          body: formData
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur upload photo:', error);
        throw error;
      }
    },

    // DELETE - Supprimer photo
    deletePhoto: async (employeeId) => {
      try {
        const response = await fetch(`${API_URL}/api/employees/${employeeId}/photo`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur suppression photo:', error);
        throw error;
      }
    },

    // POST - Vérifier le badge d'un employé
    verifyBadge: async (badgeNumber) => {
      try {
        const response = await fetch(`${API_URL}/api/employees/verify-badge`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ badge_number: badgeNumber })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur vérification badge:', error);
        throw error;
      }
    },

    // GET - Générer un badge automatique
    generateBadge: async () => {
      try {
        console.log('🎫 [API] Génération badge automatique...');

        const response = await fetch(`${API_URL}/api/employees/generate-badge`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Badge généré:', data.badge);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur génération badge:', error);
        throw error;
      }
    },

    // POST - Upload document employé
    uploadDocument: async (employeeId, formData, type) => {
      try {
        console.log('📎 [API] Upload document pour employé:', employeeId, '- Type:', type);

        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': token ? `Bearer ${token}` : ''
        };

        const response = await fetch(`${API_URL}/api/employees/${employeeId}/documents/${type}`, {
          method: 'POST',
          headers: headers,
          body: formData
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Document uploadé:', data);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur upload document:', error);
        throw error;
      }
    },

    // GET - Télécharger document employé
    downloadDocument: async (employeeId, docId) => {
      try {
        console.log('📥 [API] Téléchargement document:', docId);

        const response = await fetch(`${API_URL}/api/employees/${employeeId}/documents/${docId}`, {
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error('Erreur lors du téléchargement');
        }

        const blob = await response.blob();

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'document.pdf';

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        console.log('✅ [API] Document téléchargé:', filename);

        return { success: true };
      } catch (error) {
        console.error('❌ [API] Erreur téléchargement document:', error);
        throw error;
      }
    },

    // DELETE - Supprimer document employé
    deleteDocument: async (employeeId, docId) => {
      try {
        console.log('🗑️ [API] Suppression document:', docId);

        const response = await fetch(`${API_URL}/api/employees/${employeeId}/documents/${docId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Document supprimé');

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur suppression document:', error);
        throw error;
      }
    },

    // POST - Générer contrat employé
    generateEmployeeContract: async (contractData) => {
      try {
        console.log('📄 [API] Génération contrat employé...');

        const response = await fetch(`${API_URL}/api/employees/generate-contract`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(contractData)
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Contrat généré:', data);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur génération contrat:', error);
        throw error;
      }
    },

    // GET - Liste documents employé
    getEmployeeDocuments: async (employeeId) => {
      try {
        console.log('📡 API: Récupération documents employé', employeeId);

        const response = await fetch(`${API_URL}/api/employees/${employeeId}/documents`, {
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);

        console.log('📡 API: Documents reçus:', data.documents ? data.documents.length : 0);

        return data;
      } catch (error) {
        console.error('❌ Erreur API getEmployeeDocuments:', error);
        throw error;
      }
    },

    telechargerRapportPDF: async (type = 'all', value = '') => {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/employees/rapport-pdf?type=${type}&value=${encodeURIComponent(value)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!response.ok) throw new Error('Erreur téléchargement');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'registre-personnel.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },



  },

  // ==========================================================================
  // PARAMÈTRE CONTRAT
  // ==========================================================================

  parametreContrat: {
    get: async () => {
      try {
        console.log('📄 [API] Récupération paramètres contrat...');

        const response = await fetch(`${API_URL}/api/parametrecontrat`, {
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Paramètres contrat récupérés');

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur récupération paramètres:', error);
        throw error;
      }
    },

    update: async (updateData) => {
      try {
        console.log('📝 [API] Mise à jour paramètres contrat...');

        const response = await fetch(`${API_URL}/api/parametrecontrat`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData)
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Paramètres contrat mis à jour');

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur mise à jour paramètres:', error);
        throw error;
      }
    }
  },

  contractTypes: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/api/contract-types`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    create: async (data) => {
      const response = await fetch(`${API_URL}/api/contract-types`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    update: async (id, data) => {
      const response = await fetch(`${API_URL}/api/contract-types/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    delete: async (id) => {
      const response = await fetch(`${API_URL}/api/contract-types/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },

  contractEvents: {
    getAll: async (employeeId) => {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/contract-events`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    action: async (employeeId, data) => {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/contract-action`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    }
  },

  // ==========================================================================
  // DÉPARTEMENTS
  // ==========================================================================

  departements: {
    getAll: async () => {
      try {
        console.log('📋 [API] Récupération départements...');
        const response = await fetch(`${API_URL}/api/departments`, {
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Départements récupérés:', data.data?.length || 0);
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur récupération départements:', error);
        throw error;
      }
    },

    create: async (departmentData) => {
      try {
        console.log('➕ [API] Création département:', departmentData.name);
        const response = await fetch(`${API_URL}/api/departments`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(departmentData)
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Département créé');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur création département:', error);
        throw error;
      }
    },

    update: async (departmentId, departmentData) => {
      try {
        console.log('📝 [API] Modification département:', departmentId);
        const response = await fetch(`${API_URL}/api/departments/${departmentId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(departmentData)
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Département modifié');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur modification département:', error);
        throw error;
      }
    },

    delete: async (departmentId) => {
      try {
        console.log('🗑️ [API] Suppression département:', departmentId);
        const response = await fetch(`${API_URL}/api/departments/${departmentId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Département supprimé');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur suppression département:', error);
        throw error;
      }
    },

    initDefaults: async () => {
      try {
        console.log('🏗️ [API] Initialisation départements par défaut...');
        const response = await fetch(`${API_URL}/api/departments/init-defaults`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Départements par défaut initialisés');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur initialisation départements:', error);
        throw error;
      }
    }
  },

  // ==========================================================================
  // POINTAGES
  // ==========================================================================

  pointages: {
    getEmployeesPourPointage: async (date, page = 1, search = '', department = '', limit = 10) => {
      const params = new URLSearchParams({ date, page, limit });
      if (search) params.append('search', search);
      if (department) params.append('department', department);
      const response = await fetch(`${API_URL}/api/pointages/employees-pour-pointage?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    enregistrerPointageEmployee: async (data) => {
      const response = await fetch(`${API_URL}/api/pointages/employee`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    enregistrerLotEmployees: async (data) => {
      const response = await fetch(`${API_URL}/api/pointages/lot-employees`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    getHistoriqueByDate: async (date, page = 1, search = '', department = '', limit = 10) => {
      const params = new URLSearchParams({ date, page, limit });
      if (search) params.append('search', search);
      if (department) params.append('department', department);
      const response = await fetch(`${API_URL}/api/pointages/historique?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    envoyerComptabilite: async (pointageId) => {
      const response = await fetch(`${API_URL}/api/pointages/envoyer-comptabilite/${pointageId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    envoyerTousComptabilite: async (date) => {
      const response = await fetch(`${API_URL}/api/pointages/envoyer-tous-comptabilite?date=${date}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },


    envoyerLotComptabilite: async (pointageIds) => {
      const response = await fetch(`${API_URL}/api/pointages/envoyer-lot-comptabilite`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ pointage_ids: pointageIds })
      });
      return handleResponse(response);
    },


    // Pointage employé AVEC photo anti-fraude (multipart/form-data)
    enregistrerAvecPhoto: async (data, photoBlob) => {
      const formData = new FormData();
      formData.append('employee_id', data.employee_id);
      formData.append('date_pointage', data.date_pointage);
      formData.append('heure_pointage', data.heure_pointage);
      formData.append('timezone', data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      formData.append('statut', data.statut || 'present');
      if (photoBlob) {
        formData.append('photo_pointage', photoBlob, 'photo_pointage.jpg');
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pointages/employee-with-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        // PAS de Content-Type ici — FormData le gère automatiquement
        body: formData
      });
      return handleResponse(response);
    },


    // Télécharger rapport PDF
    telechargerRapport: async (date, type = 'all', value = '') => {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/pointages/rapport-pdf?date=${date}&type=${type}&value=${encodeURIComponent(value)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Erreur téléchargement rapport');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-pointage-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return { success: true };
    },


    updateObservation: async (pointageId, observation) => {
      const response = await fetch(`${API_URL}/api/pointages/${pointageId}/observation`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ observation })
      });
      return handleResponse(response);
    },


    saveJustificationGlobale: async (employeeId, datePontage, justification) => {
      const response = await fetch(`${API_URL}/api/pointages/justification-globale`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          employee_id: employeeId,
          date_pointage: datePontage,
          justification
        })
      });
      return handleResponse(response);
    },

    getJustificationsGlobales: async (date) => {
      const response = await fetch(`${API_URL}/api/pointages/justifications-globales?date=${date}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },


    getHistoriqueEmploye: async (employeeId, from, to) => {
      const response = await fetch(
        `${API_URL}/api/pointages/employee/${employeeId}/historique?from=${from}&to=${to}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    },



    calculerSalaireHoraire: async (employeeId, mois, annee) => {
      const response = await fetch(`${API_URL}/api/pointages/calcul-salaire/${employeeId}/${mois}/${annee}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    calculerSalairesMois: async (mois, annee) => {
      const response = await fetch(`${API_URL}/api/pointages/calcul-salaires-mois/${mois}/${annee}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },


  },

  // ==========================================================================
  // DASHBOARD
  // ==========================================================================

  dashboard: {
    getStats: async () => {
      const response = await fetch(`${API_URL}/api/dashboard/stats`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },

  // ==========================================================================
  // PARAMÈTRES POINTAGE
  // ==========================================================================

  parametresPointage: {
    get: async () => {
      const response = await fetch(`${API_URL}/api/parametres-pointage`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    save: async (data) => {
      const response = await fetch(`${API_URL}/api/parametres-pointage`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    reset: async () => {
      const response = await fetch(`${API_URL}/api/parametres-pointage/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },

  // ==========================================================================
  // MODES DE PAIEMENT
  // ==========================================================================

  modesPaiement: {
    getAll: async () => {
      try {
        const response = await fetch(`${API_URL}/api/paiements/modes-paiement`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur récupération modes paiement:', error);
        return {
          success: false,
          error: error.message
        };
      }
    }
  },

  // ==========================================================================
  // PAIEMENTS SALAIRES
  // ==========================================================================

  paiementsSalaires: {
    creerPaiementEmploye: async (paiementData) => {
      try {
        const response = await fetch(`${API_URL}/api/paiements/employes/creer`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(paiementData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur création paiement employé:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    cloturerPaiementEmploye: async (paiementId) => {
      try {
        const response = await fetch(`${API_URL}/api/paiements/employes/cloturer`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ paiement_id: paiementId })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur clôture paiement employé:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    verifierPaiementEmployeExistant: async (employeId, mois, annee) => {
      try {
        const url = `${API_URL}/api/paiements/employes/verifier/${employeId}/${mois}/${annee}`;
        console.log('📡 API verifierPaiementEmployeExistant:', url);

        const response = await fetch(url, {
          headers: getAuthHeaders()
        });

        const result = await handleResponse(response);
        console.log('📡 Réponse API verifierPaiementEmployeExistant:', result);

        return result;
      } catch (error) {
        console.error('❌ Erreur vérification paiement employé:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    getHistoriquePaiementsEmployes: async () => {
      try {
        const response = await fetch(`${API_URL}/api/paiements/employes/historique`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur historique:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    exportBulletin: async (paiementId) => {
      const response = await fetch(`${API_URL}/api/compta-pdf/bulletin/${paiementId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] || `bulletin-paie-${paiementId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

  },

  // ==========================================================================
  // COMPTABILITÉ
  // ==========================================================================

  comptabilite: {
    getPointagesMensuelsEmployes: async (mois, annee) => {
      try {
        const response = await fetch(`${API_URL}/api/pointages/employes/mensuels/${mois}/${annee}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur récupération pointages mensuels:', error);
        return {
          success: false,
          error: error.message
        };
      }
    }
  },

  // ==========================================================================
  // AVANCES SALAIRE
  // ==========================================================================

  avancesSalaire: {
    getHistoriqueAvances: async (employeId) => {
      try {
        const response = await fetch(`${API_URL}/api/avances/employee/${employeId}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur historique avances:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    getAvancesMoisEmploye: async (employeId, mois, annee) => {
      try {
        const response = await fetch(`${API_URL}/api/avances/employee/${employeId}/month/${mois}/${annee}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur avances du mois:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    creerAvanceEmploye: async (avanceData) => {
      try {
        const response = await fetch(`${API_URL}/api/avances/create`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(avanceData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur création avance:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    modifierAvance: async (avanceId, avanceData) => {
      try {
        const response = await fetch(`${API_URL}/api/avances/${avanceId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(avanceData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur modification avance:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    supprimerAvance: async (avanceId, justification) => {
      try {
        const response = await fetch(`${API_URL}/api/avances/${avanceId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({ justification_suppression: justification })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur suppression avance:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    marquerAvanceAppliquee: async (avanceId) => {
      try {
        const response = await fetch(`${API_URL}/api/avances/mark-applied/${avanceId}`, {
          method: 'PUT',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur marquage avance:', error);
        return {
          success: false,
          error: error.message
        };
      }
    }
  },

  // ==========================================================================
  // PARAMÈTRES ENTREPRISE
  // ==========================================================================


  parametres: {
    getEntreprise: async () => {
      try {
        const response = await fetch(`${API_URL}/api/parametres/entreprise`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getEntreprise:', error);
        return {
          success: false,
          message: error.message,
          data: {}
        };
      }
    },

    updateEntreprise: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/parametres/entreprise`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur updateEntreprise:', error);
        return {
          success: false,
          message: error.message
        };
      }
    },

    uploadLogoEntreprise: async (file) => {
      try {
        const formData = new FormData();
        formData.append('logo', file);

        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': token ? `Bearer ${token}` : ''
        };

        const response = await fetch(`${API_URL}/api/parametres/entreprise/upload-logo`, {
          method: 'POST',
          headers: headers,
          body: formData
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur uploadLogo:', error);
        return {
          success: false,
          message: error.message
        };
      }
    },

    deleteLogoEntreprise: async () => {
      try {
        const response = await fetch(`${API_URL}/api/parametres/entreprise/logo`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur deleteLogo:', error);
        return {
          success: false,
          message: error.message
        };
      }
    }
  },



  // ==========================================================================
  // UTILISATEURS
  // ==========================================================================

  users: {
    getAll: async () => {
      try {
        console.log('📋 [API] Récupération utilisateurs...');
        const response = await fetch(`${API_URL}/api/users/company`, {
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Utilisateurs récupérés:', data.users?.length || 0);
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur récupération utilisateurs:', error);
        throw error;
      }
    },

    getOne: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/users/${id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur récupération utilisateur:', error);
        throw error;
      }
    },

    create: async (userData) => {
      try {
        console.log('➕ [API] Création utilisateur...');
        const response = await fetch(`${API_URL}/api/users`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(userData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur création utilisateur:', error);
        throw error;
      }
    },

    update: async (id, userData) => {
      try {
        console.log('📝 [API] Modification utilisateur:', id);
        const response = await fetch(`${API_URL}/api/users/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(userData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur modification utilisateur:', error);
        throw error;
      }
    },

    delete: async (id) => {
      try {
        console.log('🗑️ [API] Suppression utilisateur:', id);
        const response = await fetch(`${API_URL}/api/users/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur suppression utilisateur:', error);
        throw error;
      }
    },

    resetPassword: async (id, password) => {
      try {
        console.log('🔑 [API] Réinitialisation mot de passe:', id);
        const response = await fetch(`${API_URL}/api/users/${id}/password`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ password })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur réinitialisation mot de passe:', error);
        throw error;
      }
    },


    getQuota: async () => {
      try {
        console.log('📊 [API] Récupération quota utilisateurs...');
        const response = await fetch(`${API_URL}/api/users/quota`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur récupération quota:', error);
        throw error;
      }
    },


    uploadPhoto: async (userId, formData) => {
      try {
        console.log('📸 [API] Upload photo utilisateur:', userId);

        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': token ? `Bearer ${token}` : ''
        };

        const response = await fetch(`${API_URL}/api/users/${userId}/photo`, {
          method: 'POST',
          headers: headers,
          body: formData
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur upload photo utilisateur:', error);
        throw error;
      }
    },

    // ✅ NOUVEAU : Supprimer photo utilisateur
    deletePhoto: async (userId) => {
      try {
        console.log('🗑️ [API] Suppression photo utilisateur:', userId);

        const response = await fetch(`${API_URL}/api/users/${userId}/photo`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur suppression photo utilisateur:', error);
        throw error;
      }
    },


    // ✅ GESTION DOCUMENTS UTILISATEURS

    getDocuments: async (userId) => {
      try {
        console.log('📂 [API] Récupération documents utilisateur:', userId);
        const response = await fetch(`${API_URL}/api/users/${userId}/documents`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur récupération documents:', error);
        throw error;
      }
    },

    uploadDocument: async (userId, formData, type) => {
      try {
        console.log('📤 [API] Upload document utilisateur:', userId, 'type:', type);

        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': token ? `Bearer ${token}` : ''
        };

        const response = await fetch(`${API_URL}/api/users/${userId}/documents?type=${type}`, {
          method: 'POST',
          headers: headers,
          body: formData
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur upload document:', error);
        throw error;
      }
    },

    downloadDocument: async (userId, docId) => {
      try {
        console.log('⬇️ [API] Téléchargement document:', docId);

        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/users/${userId}/documents/${docId}/download`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors du téléchargement');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `document_${docId}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true };
      } catch (error) {
        console.error('❌ [API] Erreur téléchargement document:', error);
        throw error;
      }
    },

    deleteDocument: async (userId, docId) => {
      try {
        console.log('🗑️ [API] Suppression document:', docId);
        const response = await fetch(`${API_URL}/api/users/${userId}/documents/${docId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur suppression document:', error);
        throw error;
      }
    },


    generateUserContract: async (contractData) => {
      try {
        console.log('📄 [API] Génération contrat utilisateur...');
        const response = await fetch(`${API_URL}/api/users/generate-contract`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(contractData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur génération contrat utilisateur:', error);
        throw error;
      }
    },


  },



  moduleRights: {
    // Liste des modules disponibles
    getModules: async () => {
      try {
        console.log('📋 [API] Récupération liste des modules...');
        const response = await fetch(`${API_URL}/api/module-rights/modules`, {
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Modules récupérés:', data.modules?.length || 0);
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur récupération modules:', error);
        throw error;
      }
    },

    // Droits d'un utilisateur spécifique
    getUserRights: async (userId) => {
      try {
        console.log('🔍 [API] Récupération droits user:', userId);
        const response = await fetch(`${API_URL}/api/module-rights/user/${userId}`, {
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Droits récupérés');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur récupération droits:', error);
        throw error;
      }
    },

    // Mes propres droits (utilisateur connecté)
    getMyRights: async () => {
      try {
        console.log('🔍 [API] Récupération de mes droits...');
        const response = await fetch(`${API_URL}/api/module-rights/my-rights`, {
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Mes droits récupérés');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur récupération de mes droits:', error);
        throw error;
      }
    },

    // Mettre à jour les droits d'un utilisateur
    updateUserRights: async (userId, rights) => {
      try {
        console.log('📝 [API] Mise à jour droits user:', userId);
        const response = await fetch(`${API_URL}/api/module-rights/user/${userId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ rights })
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Droits mis à jour');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur mise à jour droits:', error);
        throw error;
      }
    },

    // Réinitialiser les droits (accès total)
    resetUserRights: async (userId) => {
      try {
        console.log('🔄 [API] Réinitialisation droits user:', userId);
        const response = await fetch(`${API_URL}/api/module-rights/user/${userId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        console.log('✅ [API] Droits réinitialisés');
        return data;
      } catch (error) {
        console.error('❌ [API] Erreur réinitialisation droits:', error);
        throw error;
      }
    }
  },
  // ==========================================================================
  // SESSIONS MANAGEMENT
  // ==========================================================================

  sessions: {
    getMySessions: async () => {
      try {
        console.log('🔍 [API] Récupération de mes sessions...');

        const response = await fetch(`${API_URL}/api/sessions/my-sessions`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Sessions récupérées:', data.data?.totalSessions || 0);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur récupération sessions:', error);
        throw error;
      }
    },

    getSessionsCount: async () => {
      try {
        console.log('🔢 [API] Comptage des sessions...');

        const response = await fetch(`${API_URL}/api/sessions/count`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Count:', data.data?.activeSessions || 0);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur count sessions:', error);
        throw error;
      }
    },

    getSessionsStats: async () => {
      try {
        console.log('📊 [API] Récupération stats sessions...');

        const response = await fetch(`${API_URL}/api/sessions/stats`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Stats récupérées');

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur stats sessions:', error);
        throw error;
      }
    },

    deleteSession: async (sessionId) => {
      try {
        console.log('🗑️ [API] Suppression session:', sessionId.substring(0, 12) + '...');

        const response = await fetch(`${API_URL}/api/sessions/${sessionId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Session supprimée');

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur suppression session:', error);
        throw error;
      }
    },

    deleteAllOtherSessions: async () => {
      try {
        console.log('🗑️ [API] Suppression de toutes les autres sessions...');

        const response = await fetch(`${API_URL}/api/sessions/all/others`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        const data = await handleResponse(response);

        console.log('✅ [API] Autres sessions supprimées:', data.data?.deletedSessions || 0);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur suppression autres sessions:', error);
        throw error;
      }
    }
  },



  rapports: {
    // Rapport journalier
    getRapportJournalier: async (date) => {
      try {
        const response = await fetch(`${API_URL}/api/rapports/journalier?date=${date}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getRapportJournalier:', error);
        return {
          success: false,
          message: error.message,
          data: null
        };
      }
    },

    // Rapport mensuel (liste des jours du mois)
    getRapportMensuel: async (mois, annee) => {
      try {
        const response = await fetch(`${API_URL}/api/rapports/mensuel?mois=${mois}&annee=${annee}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getRapportMensuel:', error);
        return {
          success: false,
          message: error.message,
          data: null
        };
      }
    },

    // 🆕 Statistiques globales (employés, masse salariale, présence)
    getStatistiquesGlobales: async (mois, annee) => {
      try {
        const response = await fetch(`${API_URL}/api/rapports/statistiques-globales?mois=${mois}&annee=${annee}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getStatistiquesGlobales:', error);
        return {
          success: false,
          message: error.message,
          data: null
        };
      }
    },

    // 🆕 Statistiques paiements
    getStatistiquesPaiements: async (mois, annee) => {
      try {
        const response = await fetch(`${API_URL}/api/rapports/statistiques-paiements?mois=${mois}&annee=${annee}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getStatistiquesPaiements:', error);
        return {
          success: false,
          message: error.message,
          data: null
        };
      }
    },

    // 🆕 Statistiques avances
    getStatistiquesAvances: async (mois, annee) => {
      try {
        const response = await fetch(`${API_URL}/api/rapports/statistiques-avances?mois=${mois}&annee=${annee}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getStatistiquesAvances:', error);
        return {
          success: false,
          message: error.message,
          data: null
        };
      }
    },

    exportPdfJournalier: async (date) => {
      const response = await fetch(`${API_URL}/api/rapports/export-pdf-journalier?date=${date}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-journalier-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

    exportPdfMensuel: async (mois, annee) => {
      const response = await fetch(`${API_URL}/api/rapports/export-pdf-mensuel?mois=${mois}&annee=${annee}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-mensuel-${String(mois).padStart(2, '0')}-${annee}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },


    getEmployesFantomes: async () => {
      const response = await fetch(`${API_URL}/api/rapports/employes-fantomes`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },


  },



  documents: {
    // DOSSIERS
    getDossiers: async () => {
      try {
        const response = await fetch(`${API_URL}/api/documents/dossiers`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getDossiers:', error);
        return { success: false, message: error.message, data: [] };
      }
    },

    createDossier: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/documents/dossiers`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur createDossier:', error);
        return { success: false, message: error.message };
      }
    },

    updateDossier: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/api/documents/dossiers/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur updateDossier:', error);
        return { success: false, message: error.message };
      }
    },

    deleteDossier: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/documents/dossiers/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur deleteDossier:', error);
        return { success: false, message: error.message };
      }
    },

    // FICHIERS
    getFichiers: async (dossierId = null, search = null) => {
      try {
        let url = `${API_URL}/api/documents/fichiers?`;
        if (dossierId) url += `dossier_id=${dossierId}&`;
        if (search) url += `search=${encodeURIComponent(search)}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getFichiers:', error);
        return { success: false, message: error.message, data: [] };
      }
    },

    uploadFichier: async (formData) => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/documents/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur uploadFichier:', error);
        return { success: false, message: error.message };
      }
    },



    downloadFichier: async (id, nomOriginal) => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/documents/download/${id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error('Erreur téléchargement');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nomOriginal || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (error) {
        console.error('❌ Erreur downloadFichier:', error);
        return { success: false, message: error.message };
      }
    },

    // NOUVELLE MÉTHODE - Prévisualisation
    getPreviewBlob: async (id) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/documents/preview/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Erreur prévisualisation');
      const blob = await response.blob();
      return { blob, contentType: response.headers.get('Content-Type') };
    },


    deleteFichier: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/documents/fichiers/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur deleteFichier:', error);
        return { success: false, message: error.message };
      }
    }
  },



  //PARTIE COMPTA 

  tresorerie: {
    getCaisses: async () => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/caisses`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getCaisses:', error);
        return { success: false, message: error.message, data: [] };
      }
    },

    getCaisse: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/caisses/${id}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getCaisse:', error);
        return { success: false, message: error.message };
      }
    },

    creerCaisse: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/caisses`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur creerCaisse:', error);
        return { success: false, message: error.message };
      }
    },

    modifierCaisse: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/caisses/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur modifierCaisse:', error);
        return { success: false, message: error.message };
      }
    },

    archiverCaisse: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/caisses/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur archiverCaisse:', error);
        return { success: false, message: error.message };
      }
    },

    getMouvements: async (caisseId, params = {}) => {
      try {
        let url = `${API_URL}/api/tresorerie/mouvements/${caisseId}?`;
        if (params.limit) url += `limit=${params.limit}&`;
        if (params.offset) url += `offset=${params.offset}&`;
        if (params.date_debut) url += `date_debut=${params.date_debut}&`;
        if (params.date_fin) url += `date_fin=${params.date_fin}&`;
        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getMouvements:', error);
        return { success: false, message: error.message, data: [] };
      }
    },

    creerMouvement: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/mouvements`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur creerMouvement:', error);
        return { success: false, message: error.message };
      }
    },

    supprimerMouvement: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/mouvements/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur supprimerMouvement:', error);
        return { success: false, message: error.message };
      }
    },

    getCategories: async () => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/categories`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getCategories:', error);
        return { success: false, message: error.message, data: [] };
      }
    },

    getDashboard: async () => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/dashboard`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getDashboard:', error);
        return { success: false, message: error.message };
      }
    },



    creerTransfert: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/tresorerie/transferts`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur creerTransfert:', error);
        return { success: false, message: error.message };
      }
    },

    getTransferts: async (params = {}) => {
      try {
        let url = `${API_URL}/api/tresorerie/transferts?`;
        if (params.caisse_id) url += `caisse_id=${params.caisse_id}&`;
        if (params.limit) url += `limit=${params.limit}&`;
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getTransferts:', error);
        return { success: false, message: error.message, data: [] };
      }
    },





  },


  partenaires: {
    getAll: async (params = {}) => {
      try {
        let url = `${API_URL}/api/partenaires?`;
        if (params.type) url += `type=${params.type}&`;
        if (params.search) url += `search=${encodeURIComponent(params.search)}&`;
        if (params.tri) url += `tri=${params.tri}&`;
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message, data: [] }; }
    },
    getStats: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/partenaires/${id}/stats`, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    getOne: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/partenaires/${id}`, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getOne partenaire:', error);
        return { success: false, message: error.message };
      }
    },
    creer: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/partenaires`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur creer partenaire:', error);
        return { success: false, message: error.message };
      }
    },
    modifier: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/api/partenaires/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur modifier partenaire:', error);
        return { success: false, message: error.message };
      }
    },
    archiver: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/partenaires/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur archiver partenaire:', error);
        return { success: false, message: error.message };
      }
    },

    exportPDF: async (id) => {
      const response = await fetch(`${API_URL}/api/compta-pdf/partenaire/${id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] || `fiche-partenaire-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

  },


  facturation: {
    getAll: async (params = {}) => {
      try {
        let url = `${API_URL}/api/facturation?`;
        if (params.type) url += `type=${params.type}&`;
        if (params.statut) url += `statut=${params.statut}&`;
        if (params.search) url += `search=${encodeURIComponent(params.search)}&`;
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message, data: [] }; }
    },
    getOne: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/facturation/${id}`, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    creer: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/facturation`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    modifier: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/api/facturation/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },


    changerStatut: async (id, statut) => {
      try {
        const response = await fetch(`${API_URL}/api/facturation/${id}/statut`, {
          method: 'PUT',  // ← changer PATCH en PUT
          headers: getAuthHeaders(),
          body: JSON.stringify({ statut })
        });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },

    enregistrerPaiement: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/api/facturation/${id}/paiement`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    convertirDevis: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/facturation/${id}/convertir`, { method: 'POST', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },


    exportPDF: async (id) => {
      const response = await fetch(`${API_URL}/api/compta-pdf/facture/${id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] || `document-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },


  },



  reporting: {
    getGlobal: async (params = {}) => {
      try {
        let url = `${API_URL}/api/reporting/global?`;
        if (params.mois) url += `mois=${params.mois}&`;
        if (params.annee) url += `annee=${params.annee}&`;
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getGlobal:', error);
        return { success: false, message: error.message };
      }
    },
  },


  validation: {
    getAll: async (params = {}) => {
      try {
        let url = `${API_URL}/api/validation?`;
        if (params.statut) url += `statut=${params.statut}&`;
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message, data: [] }; }
    },
    creer: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/validation`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    approuver: async (id, data = {}) => {
      try {
        const response = await fetch(`${API_URL}/api/validation/${id}/approuver`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    rejeter: async (id, data = {}) => {
      try {
        const response = await fetch(`${API_URL}/api/validation/${id}/rejeter`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    executer: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/api/validation/${id}/executer`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
  },


  commandes: {
    getAll: async (params = {}) => {
      try {
        let url = `${API_URL}/api/commandes?`;
        if (params.type) url += `type=${params.type}&`;
        if (params.statut) url += `statut=${params.statut}&`;
        if (params.search) url += `search=${encodeURIComponent(params.search)}&`;
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message, data: [] }; }
    },
    getOne: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/commandes/${id}`, { method: 'GET', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    creer: async (data) => {
      try {
        const response = await fetch(`${API_URL}/api/commandes`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    changerStatut: async (id, statut) => {
      try {
        const response = await fetch(`${API_URL}/api/commandes/${id}/statut`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ statut }) });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },
    convertirEnFacture: async (id) => {
      try {
        const response = await fetch(`${API_URL}/api/commandes/${id}/convertir`, { method: 'POST', headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) { return { success: false, message: error.message }; }
    },


    exportPDF: async (id) => {
      const response = await fetch(`${API_URL}/api/compta-pdf/commande/${id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] || `commande-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

  },




  livraisons: {
    getAll: async (params = {}) => {
      let url = `${API_URL}/api/livraisons?`;
      if (params.type) url += `type=${params.type}&`;
      if (params.statut) url += `statut=${params.statut}&`;
      if (params.search) url += `search=${encodeURIComponent(params.search)}&`;
      try { return handleResponse(await fetch(url, { headers: getAuthHeaders() })); } catch (e) { return { success: false, data: [] }; }
    },
    getOne: async (id) => {
      try { return handleResponse(await fetch(`${API_URL}/api/livraisons/${id}`, { headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },
    fromCommande: async (commandeId) => {
      try { return handleResponse(await fetch(`${API_URL}/api/livraisons/from-commande/${commandeId}`, { headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },
    creer: async (data) => {
      try { return handleResponse(await fetch(`${API_URL}/api/livraisons`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) })); } catch (e) { return { success: false }; }
    },
    changerStatut: async (id, statut) => {
      try { return handleResponse(await fetch(`${API_URL}/api/livraisons/${id}/statut`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ statut }) })); } catch (e) { return { success: false }; }
    },

    exportPDF: async (id) => {
      const response = await fetch(`${API_URL}/api/compta-pdf/livraison/${id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] || `livraison-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  },


  planComptable: {
    init: async () => {
      try { return handleResponse(await fetch(`${API_URL}/api/plan-comptable/init`, { method: 'POST', headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },
    getAll: async (params = {}) => {
      let url = `${API_URL}/api/plan-comptable?`;
      if (params.classe) url += `classe=${params.classe}&`;
      if (params.search) url += `search=${encodeURIComponent(params.search)}&`;
      if (params.type_compte) url += `type_compte=${params.type_compte}&`;
      try { return handleResponse(await fetch(url, { headers: getAuthHeaders() })); } catch (e) { return { success: false, data: [] }; }
    },
    creer: async (data) => {
      try { return handleResponse(await fetch(`${API_URL}/api/plan-comptable`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) })); } catch (e) { return { success: false }; }
    },
    modifier: async (id, data) => {
      try { return handleResponse(await fetch(`${API_URL}/api/plan-comptable/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) })); } catch (e) { return { success: false }; }
    },
    supprimer: async (id) => {
      try { return handleResponse(await fetch(`${API_URL}/api/plan-comptable/${id}`, { method: 'DELETE', headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },
  },



  stocks: {
    getEntrepots: async () => {
      try { return handleResponse(await fetch(`${API_URL}/api/stocks/entrepots`, { headers: getAuthHeaders() })); } catch (e) { return { success: false, data: [] }; }
    },
    creerEntrepot: async (data) => {
      try { return handleResponse(await fetch(`${API_URL}/api/stocks/entrepots`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) })); } catch (e) { return { success: false }; }
    },
    getArticles: async (params = {}) => {
      let url = `${API_URL}/api/stocks/articles?`;
      if (params.search) url += `search=${encodeURIComponent(params.search)}&`;
      if (params.categorie) url += `categorie=${encodeURIComponent(params.categorie)}&`;
      if (params.alerte) url += `alerte=1&`;
      try { return handleResponse(await fetch(url, { headers: getAuthHeaders() })); } catch (e) { return { success: false, data: [] }; }
    },
    getArticle: async (id) => {
      try { return handleResponse(await fetch(`${API_URL}/api/stocks/articles/${id}`, { headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },
    creerArticle: async (data) => {
      try { return handleResponse(await fetch(`${API_URL}/api/stocks/articles`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) })); } catch (e) { return { success: false }; }
    },
    modifierArticle: async (id, data) => {
      try { return handleResponse(await fetch(`${API_URL}/api/stocks/articles/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) })); } catch (e) { return { success: false }; }
    },
    creerMouvement: async (data) => {
      try { return handleResponse(await fetch(`${API_URL}/api/stocks/mouvements`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) })); } catch (e) { return { success: false }; }
    },
    getDashboard: async () => {
      try { return handleResponse(await fetch(`${API_URL}/api/stocks/dashboard`, { headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },

    exportInventaire: async (filtres = {}) => {
      const params = new URLSearchParams();
      if (filtres.categorie) params.append('categorie', filtres.categorie);
      if (filtres.entrepot_id) params.append('entrepot_id', filtres.entrepot_id);
      if (filtres.alerte) params.append('alerte', '1');
      const response = await fetch(`${API_URL}/api/compta-pdf/inventaire?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] || `inventaire-stock.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

  },


  parametresCompta: {
    init: async () => {
      try { return handleResponse(await fetch(`${API_URL}/api/parametres-comptabilite/init`, { method: 'POST', headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },
    getAll: async () => {
      try { return handleResponse(await fetch(`${API_URL}/api/parametres-comptabilite`, { headers: getAuthHeaders() })); } catch (e) { return { success: false, data: {} }; }
    },
    get: async (cle) => {
      try { return handleResponse(await fetch(`${API_URL}/api/parametres-comptabilite/${cle}`, { headers: getAuthHeaders() })); } catch (e) { return { success: false }; }
    },
    update: async (parametres) => {
      try { return handleResponse(await fetch(`${API_URL}/api/parametres-comptabilite`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ parametres }) })); } catch (e) { return { success: false }; }
    },

    getDevises: async (search = '') => {
      try {
        let url = `${API_URL}/api/parametres-comptabilite/devises/list`;
        if (search) url += `?search=${encodeURIComponent(search)}`;
        return handleResponse(await fetch(url, { headers: getAuthHeaders() }));
      } catch (e) { return { success: false, data: [] }; }
    },

  },


  approbations: {
    // Liste des demandes avec filtres
    lister: async (queryString = '') => {
      const response = await fetch(`${API_URL}/api/approbations?${queryString}`, { headers: getAuthHeaders() });
      return response.json();
    },

    // Détail d'une demande
    getDetail: async (id) => {
      const response = await fetch(`${API_URL}/api/approbations/${id}`, { headers: getAuthHeaders() });
      return response.json();
    },

    // Prendre une décision (approuver / rejeter)
    decision: async (id, data) => {
      const response = await fetch(`${API_URL}/api/approbations/${id}/decision`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },

    // Annuler une demande
    annuler: async (id) => {
      const response = await fetch(`${API_URL}/api/approbations/${id}/annuler`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return response.json();
    },

    // Vérifier si approbation requise
    verifier: async (type_document, montant) => {
      const response = await fetch(`${API_URL}/api/approbations/verifier`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_document, montant })
      });
      return response.json();
    },

    // Créer une demande d'approbation
    creer: async (data) => {
      const response = await fetch(`${API_URL}/api/approbations`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },

    // Stats dashboard
    getStats: async () => {
      const response = await fetch(`${API_URL}/api/approbations/stats`, { headers: getAuthHeaders() });
      return response.json();
    },

    // Compteur en attente (pour badges)
    getEnAttente: async () => {
      const response = await fetch(`${API_URL}/api/approbations/en-attente`, { headers: getAuthHeaders() });
      return response.json();
    },

    // Configuration
    getConfig: async () => {
      const response = await fetch(`${API_URL}/api/approbations/config`, { headers: getAuthHeaders() });
      return response.json();
    },

    // Sauvegarder config d'un type
    sauvegarderConfig: async (type, data) => {
      const response = await fetch(`${API_URL}/api/approbations/config/${type}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },

    // Liste des approbateurs disponibles
    getApprobateurs: async () => {
      const response = await fetch(`${API_URL}/api/approbations/approbateurs`, { headers: getAuthHeaders() });
      return response.json();
    },
  },


  // ==========================================================================
  // FEATURE FLAGS
  // ==========================================================================

  features: {
    /**
     * Vérifier le statut d'une feature pour l'entreprise courante
     */
    checkStatus: async (featureName) => {
      try {
        console.log(`🔍 [API] Check feature: ${featureName}`);

        const response = await fetch(`${API_URL}/api/features/check/${featureName}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);

        console.log(`✅ [API] Feature ${featureName}:`, data.data.enabled);

        return data;
      } catch (error) {
        console.error(`❌ [API] Erreur check feature:`, error);
        throw error;
      }
    },

    /**
     * Lister toutes les features (admin)
     */
    listAll: async () => {
      try {
        console.log('📋 [API] Liste des features...');

        const response = await fetch(`${API_URL}/api/features`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);

        console.log(`✅ [API] ${data.data.length} features trouvées`);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur list features:', error);
        throw error;
      }
    },

    /**
     * Lister les entreprises (pour dropdown admin)
     */
    listCompanies: async () => {
      try {
        const response = await fetch(`${API_URL}/api/features/companies`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur list companies:', error);
        throw error;
      }
    },

    /**
     * Activer une feature (admin)
     */
    enableFeature: async (featureName, companyId, scope = 'company') => {
      try {
        console.log(`🔓 [API] Activation feature: ${featureName}`, { companyId, scope });

        const response = await fetch(`${API_URL}/api/features/enable`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ featureName, companyId, scope })
        });

        const data = await handleResponse(response);

        console.log(`✅ [API] Feature activée:`, data.message);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur enable feature:', error);
        throw error;
      }
    },

    /**
     * Désactiver une feature (admin)
     */
    disableFeature: async (featureName, companyId, scope = 'company') => {
      try {
        console.log(`🔒 [API] Désactivation feature: ${featureName}`, { companyId, scope });

        const response = await fetch(`${API_URL}/api/features/disable`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ featureName, companyId, scope })
        });

        const data = await handleResponse(response);

        console.log(`✅ [API] Feature désactivée:`, data.message);

        return data;
      } catch (error) {
        console.error('❌ [API] Erreur disable feature:', error);
        throw error;
      }
    },

    getUserFlags: async () => {
      const response = await fetch(`${API_URL}/api/features/user`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },


  },



  communications: {

    // GET — Communications actives ciblées pour l'entreprise connectée
    // Inclut le flag is_read par communication
    getAll: async () => {
      const response = await fetch(`${API_URL}/api/communications`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // POST — Marquer une communication comme lue (idempotent)
    markAsRead: async (communicationId) => {
      const response = await fetch(`${API_URL}/api/communications/${communicationId}/read`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }

  },


  // ==========================================================================
  // COMPTA PAIEMENT HORAIRE 
  // ==========================================================================


  groupesSalariaux: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/api/groupes-salariaux`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    create: async (data) => {
      const response = await fetch(`${API_URL}/api/groupes-salariaux`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    update: async (id, data) => {
      const response = await fetch(`${API_URL}/api/groupes-salariaux/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    delete: async (id) => {
      const response = await fetch(`${API_URL}/api/groupes-salariaux/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
  },

  paliersDeduction: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/api/paliers-deduction`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    create: async (data) => {
      const response = await fetch(`${API_URL}/api/paliers-deduction`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    delete: async (id) => {
      const response = await fetch(`${API_URL}/api/paliers-deduction/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
  },


  primes: {
    getAll: async (mois, annee) => {
      const params = mois && annee ? `?mois=${mois}&annee=${annee}` : '';
      const response = await fetch(`${API_URL}/api/primes${params}`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    getByEmploye: async (employeId) => {
      const response = await fetch(`${API_URL}/api/primes/employe/${employeId}`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    getByEmployeMois: async (employeId, mois, annee) => {
      const response = await fetch(`${API_URL}/api/primes/employe/${employeId}/${mois}/${annee}`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    creer: async (data) => {
      const response = await fetch(`${API_URL}/api/primes`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    modifier: async (id, data) => {
      const response = await fetch(`${API_URL}/api/primes/${id}`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    annuler: async (id, justification) => {
      const response = await fetch(`${API_URL}/api/primes/${id}`, {
        method: 'DELETE', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ justification_suppression: justification })
      });
      return handleResponse(response);
    },
  },


  conges: {
    getAll: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/conges${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    getByEmploye: async (employeId, annee) => {
      const response = await fetch(`${API_URL}/api/conges/employe/${employeId}${annee ? '?annee=' + annee : ''}`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    getSolde: async (employeId, annee) => {
      const response = await fetch(`${API_URL}/api/conges/solde/${employeId}/${annee}`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    getSoldes: async (annee) => {
      const response = await fetch(`${API_URL}/api/conges/soldes/${annee}`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    getActifsAujourdhui: async () => {
      const response = await fetch(`${API_URL}/api/conges/actifs-aujourdhui`, { headers: getAuthHeaders() });
      return handleResponse(response);
    },
    creer: async (data) => {
      const response = await fetch(`${API_URL}/api/conges`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    modifier: async (id, data) => {
      const response = await fetch(`${API_URL}/api/conges/${id}`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    annuler: async (id, justification) => {
      const response = await fetch(`${API_URL}/api/conges/${id}`, {
        method: 'DELETE', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ justification_annulation: justification })
      });
      return handleResponse(response);
    },
  },

};

// ============================================================================
// HELPER FUNCTIONS UTILITAIRES
// ============================================================================

// Vérifier si l'utilisateur est authentifié
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

// Obtenir les infos utilisateur
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// 🔥 NOUVEAU : Obtenir les infos company
export const getCurrentCompany = () => {
  const companyStr = localStorage.getItem('company');
  return companyStr ? JSON.parse(companyStr) : null;
};

// Construire l'URL complète d'une image/fichier
export const getFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
};

// ============================================================================
// EXPORT
// ============================================================================

export default api;
